import { Component, computed, inject, OnInit, OnChanges, SimpleChanges, Input, Output, EventEmitter, ViewChild } from '@angular/core';
import { ReactiveFormsModule, FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { HotToastService } from '@ngxpert/hot-toast';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { forkJoin, Observable, of, map } from 'rxjs';
import { MaterialModule } from '../../../../../shared/material.module';
import { NumberFormatDirective } from '../../../../../directives/number-format.directive';
import { TabToolbarComponent } from '../tab-toolbar/tab-toolbar.component';
import { KeywordInputComponent } from '../keyword-input/keyword-input.component';
import { GenericListComponent } from '../../../../shared/generic-list/generic-list.component';
import { AutocompleteSelectComponent } from '../../../../shared/autocomplete-select/autocomplete-select.component';
import { ColumnDefinition } from '../../../../../models/columnDefinition';
import { HasUnsavedChanges } from '../../../../../guards/unsaved-changes.guard';
import { ConfirmationDialogService } from '../../../../../services/confirmation-dialog.service';
import { Helpers } from '../../../../../utilities/helpers';
import { LutService } from '../../../../../services/lut.service';
import { CompanyAuditReportService } from '../../../../../services/companies/company-audit-report.service';
import { CompanyAuditReportDto, UpsertAuditReportRequest } from '../../../../../models/api/companyAuditReportDto';
import { handleYearlyResource } from '../../../../../utilities/yearlyResource.handler';
import { AccountingStandard } from '../../../../../models/accountingStandard';
import { AuditOpinion } from '../../../../../models/auditOpinion';
import { AuditOpinionModified } from '../../../../../models/auditOpinionModified';
import { AuditOpinionQualifiedOption } from '../../../../../models/auditOpinionQualifiedOption';
import { AuditEmphasisOfMatter } from '../../../../../models/auditEmphasisOfMatter';
import { AuditKeyMatter } from '../../../../../models/auditKeyMatter';
import { AuditGoingConcern } from '../../../../../models/auditGoingConcern';
import { AuditMateriality } from '../../../../../models/auditMateriality';
import { ApiErrorResponse } from '../../../../../models/api/apiErrorResponse';
import { ErrorCodes } from '../../../../../models/api/errorCodes';
import { selectTabYear, selectDefaultYear } from '../../../../../store/selectors/company-form.selectors';
import { setTabYear } from '../../../../../store/actions/company-form.actions';
import { CompanyKeywordService } from '../../../../../services/companies/company-keyword.service';
import { CompanyKeywordDto, CompanyKeywordUpdateRequest } from '../../../../../models/api/companyKeywordDto';
import { KeywordType } from '../../../../../models/enums/keywordType.enum';
import { buildKeywordRequests } from '../../../../../utilities/keyword-helpers';
import { saveTabThenKeywords } from '../../../../../utilities/tab-save';
import { serverErrorMessage } from '../../../../../utilities/api-error';
import { CurrentUserState } from '../../../../../state/current-user.state';
import { ReadOnlyTabDirective } from '../../../../../directives/read-only-tab.directive';
import { AppUserCompanyAccessType } from '../../../../../models/enums/appUserCompanyAccessType.enum';

@Component({
  selector: 'app-audit2-tab',
  imports: [
    ReactiveFormsModule,
    MaterialModule,
    NgxSpinnerModule,
    NumberFormatDirective,
    TabToolbarComponent,
    KeywordInputComponent,
    GenericListComponent,
    AutocompleteSelectComponent,
    ReadOnlyTabDirective
  ],
  templateUrl: './audit2-tab.component.html',
  styleUrl: './audit2-tab.component.scss'
})
export class Audit2TabComponent implements OnInit, OnChanges, HasUnsavedChanges {
  @Input() keywords: CompanyKeywordDto[] = [];
  @Output() keywordsUpdated = new EventEmitter<CompanyKeywordDto[]>();

  private fb = inject(FormBuilder);
  private route = inject(ActivatedRoute);
  private store = inject(Store);
  private lutService = inject(LutService);
  private auditReportService = inject(CompanyAuditReportService);
  private companyKeywordService = inject(CompanyKeywordService);
  private confirmationDialog = inject(ConfirmationDialogService);
  private toast = inject(HotToastService);
  private spinnerService = inject(NgxSpinnerService);
  private currentUserState = inject(CurrentUserState);

  /** Tab held read-only: the form is disabled by ReadOnlyTabDirective and row actions are hidden. */
  readonly isReadOnly = computed(() =>
    this.currentUserState.isCompanyTabReadOnly(AppUserCompanyAccessType.Audit2));

  selectedYear: string = this.currentUserState.defaultMeasurementYear();
  companyId: string | null = null;
  showYearWarning = false;
  showKeywords = true;

  accountingStandards: AccountingStandard[] = [];
  auditOpinions: AuditOpinion[] = [];
  auditOpinionModifieds: AuditOpinionModified[] = [];
  allQualifiedOptions: AuditOpinionQualifiedOption[] = [];
  allEmphasisOfMatters: AuditEmphasisOfMatter[] = [];
  allKeyMatters: AuditKeyMatter[] = [];
  allGoingConcerns: AuditGoingConcern[] = [];
  auditMaterialities: AuditMateriality[] = [];

  selectedQualifiedOptions: AuditOpinionQualifiedOption[] = [];
  selectedQualifiedOptionId = '';
  selectedEmphasisOfMatters: AuditEmphasisOfMatter[] = [];
  pendingEmphasisOfMatter: AuditEmphasisOfMatter | null = null;
  selectedGoingConcerns: AuditGoingConcern[] = [];
  selectedGoingConcernId = '';
  selectedKeyMatters: AuditKeyMatter[] = [];
  pendingKeyMatter: AuditKeyMatter | null = null;

  @ViewChild('emphasisAutocomplete') emphasisAutocomplete!: AutocompleteSelectComponent;
  @ViewChild('keyMatterAutocomplete') keyMatterAutocomplete!: AutocompleteSelectComponent;

  qualifiedOptionColumnDefinitions: ColumnDefinition[] = [
    { propertyName: 'name', displayName: 'Qualified Option', dataType: 'string' }
  ];

  emphasisOfMatterColumnDefinitions: ColumnDefinition[] = [
    { propertyName: 'name', displayName: 'Emphasis of Matter', dataType: 'string' }
  ];

  keyMatterColumnDefinitions: ColumnDefinition[] = [
    { propertyName: 'name', displayName: 'Key Audit Matter', dataType: 'string' }
  ];

  goingConcernColumnDefinitions: ColumnDefinition[] = [
    { propertyName: 'name', displayName: 'Going Concern', dataType: 'string' }
  ];

  form: FormGroup = this.fb.group({
    accountingStandardId: [''],
    auditOpinionId: [''],
    auditOpinionModifiedId: [''],
    hasGoingConcern: [''],
    materialityValue: [''],
    auditMaterialityId: [''],
    keywords: this.fb.group({
      auditor: [''],
      accountingStandard: [''],
      emphasisOfMatters: [''],
      keyAuditMatters: ['']
    })
  });

  get hasChanges(): boolean {
    return this.form.dirty;
  }

  hasUnsavedChanges(): boolean {
    return this.hasChanges;
  }

  get isQualified(): boolean {
    const modifiedId = this.form.get('auditOpinionModifiedId')?.value;
    if (!modifiedId) return false;
    const modified = this.auditOpinionModifieds.find(m => m.id === modifiedId);
    return modified?.name?.toLowerCase() === 'qualified';
  }

  get isModifiedOpinion(): boolean {
    const opinionId = this.form.get('auditOpinionId')?.value;
    if (!opinionId) return false;
    const opinion = this.auditOpinions.find(o => o.id === opinionId);
    return opinion?.name?.toLowerCase() === 'modified';
  }

  get availableQualifiedOptions(): AuditOpinionQualifiedOption[] {
    const selectedIds = new Set(this.selectedQualifiedOptions.map(o => o.id));
    return this.allQualifiedOptions.filter(o => !selectedIds.has(o.id));
  }

  get availableEmphasisOfMatters(): AuditEmphasisOfMatter[] {
    const selectedIds = new Set(this.selectedEmphasisOfMatters.map(o => o.id));
    return this.allEmphasisOfMatters.filter(o => !selectedIds.has(o.id));
  }

  get isGoingConcernYes(): boolean {
    return this.form.get('hasGoingConcern')?.value === 'yes';
  }

  get availableGoingConcerns(): AuditGoingConcern[] {
    const selectedIds = new Set(this.selectedGoingConcerns.map(o => o.id));
    return this.allGoingConcerns.filter(o => !selectedIds.has(o.id));
  }

  get availableKeyMatters(): AuditKeyMatter[] {
    const selectedIds = new Set(this.selectedKeyMatters.map(o => o.id));
    return this.allKeyMatters.filter(o => !selectedIds.has(o.id));
  }

  ngOnInit(): void {
    this.companyId = this.route.snapshot.paramMap.get('id') ?? null;

    const storedYear = this.store.selectSignal(selectTabYear('audit2'))();
    const defaultYear = this.store.selectSignal(selectDefaultYear)();
    if (storedYear) {
      this.selectedYear = storedYear;
    } else if (defaultYear) {
      this.selectedYear = defaultYear;
      this.store.dispatch(setTabYear({ tabId: 'audit2', year: defaultYear }));
    } else {
      this.store.dispatch(setTabYear({ tabId: 'audit2', year: this.selectedYear }));
    }

    this.patchKeywords();

    // Opinion Modified depends on the selected Opinion — re-evaluate on change.
    this.form.get('auditOpinionId')!.valueChanges.subscribe(() => {
      this.syncOpinionModifiedState();
    });

    this.loadLookupData();
  }

  /**
   * Opinion Modified (and its Qualified Options) only applies when the
   * Opinion is "Modified". For any other Opinion the control is cleared
   * and disabled so Adverse/Disclaimer/Qualified cannot be selected.
   */
  private syncOpinionModifiedState(): void {
    const control = this.form.get('auditOpinionModifiedId')!;
    if (this.isModifiedOpinion) {
      // On a read-only tab every control stays disabled — re-enabling here would punch a hole
      // in the lock ReadOnlyTabDirective applies to the form.
      if (control.disabled && !this.isReadOnly()) {
        control.enable({ emitEvent: false });
      }
    } else {
      if (control.value) {
        control.setValue('', { emitEvent: false });
      }
      this.selectedQualifiedOptions = [];
      if (control.enabled) {
        control.disable({ emitEvent: false });
      }
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['keywords'] && !changes['keywords'].firstChange) {
      this.patchKeywords();
    }
  }

  private patchKeywords(): void {
    const auditor = this.keywords.find(k => k.type === KeywordType.AuditReportAuditor);
    const accountingStandard = this.keywords.find(k => k.type === KeywordType.AuditReportAccountingStandard);
    const emphasis = this.keywords.find(k => k.type === KeywordType.AuditReportEmphasisOfMatters);
    const keyMatters = this.keywords.find(k => k.type === KeywordType.AuditReportKeyAuditMatters);
    this.form.get('keywords')!.patchValue({
      auditor: auditor?.value ?? '',
      accountingStandard: accountingStandard?.value ?? '',
      emphasisOfMatters: emphasis?.value ?? '',
      keyAuditMatters: keyMatters?.value ?? ''
    }, { emitEvent: false });
    this.form.markAsPristine();
  }

  onYearChange(year: string): void {
    if (this.hasChanges) {
      this.confirmationDialog.openConfirmationDialog(
        'Unsaved Changes',
        'You have unsaved changes. Are you sure you want to switch measurement?',
        'Switch',
        'Stay',
        'swap_horiz',
        'edit'
      ).subscribe(confirmed => {
        if (confirmed) {
          this.applyYearChange(year);
        } else {
          const current = this.selectedYear;
          this.selectedYear = '';
          setTimeout(() => this.selectedYear = current);
        }
      });
    } else {
      this.applyYearChange(year);
    }
  }

  private applyYearChange(year: string): void {
    this.selectedYear = year;
    this.store.dispatch(setTabYear({ tabId: 'audit2', year }));
    this.loadData();
  }

  onSave(): void {
    if (!this.companyId) return;
    this.performSave();
  }

  private getSaveDescription(): string {
    const keywordsGroup = this.form.get('keywords')!;
    const hasReportChanges = this.form.dirty && (
      this.form.get('accountingStandardId')!.dirty ||
      this.form.get('auditOpinionId')!.dirty ||
      this.form.get('auditOpinionModifiedId')!.dirty ||
      this.form.get('hasGoingConcern')!.dirty ||
      this.form.get('materialityValue')!.dirty ||
      this.form.get('auditMaterialityId')!.dirty
    );
    const hasKeywordChanges = keywordsGroup.dirty;

    if (hasReportChanges && hasKeywordChanges) return 'audit report and keywords';
    if (hasKeywordChanges) return 'keywords';
    return 'audit report';
  }

  onAddQualifiedOption(): void {
    if (!this.selectedQualifiedOptionId) return;
    const option = this.allQualifiedOptions.find(o => o.id === this.selectedQualifiedOptionId);
    if (option) {
      this.selectedQualifiedOptions = this.sortByName([...this.selectedQualifiedOptions, option]);
      this.selectedQualifiedOptionId = '';
      this.form.markAsDirty();
    }
  }

  onDeleteQualifiedOption(option: AuditOpinionQualifiedOption): void {
    this.selectedQualifiedOptions = this.selectedQualifiedOptions.filter(o => o.id !== option.id);
    this.form.markAsDirty();
  }

  onEmphasisOfMatterPending(matter: AuditEmphasisOfMatter): void {
    this.pendingEmphasisOfMatter = matter ?? null;
  }

  onAddEmphasisOfMatter(): void {
    if (!this.pendingEmphasisOfMatter || this.selectedEmphasisOfMatters.some(m => m.id === this.pendingEmphasisOfMatter!.id)) return;
    this.selectedEmphasisOfMatters = this.sortByName([...this.selectedEmphasisOfMatters, this.pendingEmphasisOfMatter]);
    this.pendingEmphasisOfMatter = null;
    this.emphasisAutocomplete.searchControl.setValue('');
    this.form.markAsDirty();
  }

  onDeleteEmphasisOfMatter(matter: AuditEmphasisOfMatter): void {
    this.selectedEmphasisOfMatters = this.selectedEmphasisOfMatters.filter(m => m.id !== matter.id);
    this.form.markAsDirty();
  }

  onAddGoingConcern(): void {
    if (!this.selectedGoingConcernId) return;
    const concern = this.allGoingConcerns.find(c => c.id === this.selectedGoingConcernId);
    if (concern) {
      this.selectedGoingConcerns = this.sortByName([...this.selectedGoingConcerns, concern]);
      this.selectedGoingConcernId = '';
      this.form.markAsDirty();
    }
  }

  onDeleteGoingConcern(concern: AuditGoingConcern): void {
    this.selectedGoingConcerns = this.selectedGoingConcerns.filter(c => c.id !== concern.id);
    this.form.markAsDirty();
  }

  onKeyMatterPending(matter: AuditKeyMatter): void {
    this.pendingKeyMatter = matter ?? null;
  }

  onAddKeyMatter(): void {
    if (!this.pendingKeyMatter || this.selectedKeyMatters.some(m => m.id === this.pendingKeyMatter!.id)) return;
    // Key matters keep the order they were added in (no alphabetical sort) — append to the end.
    this.selectedKeyMatters = [...this.selectedKeyMatters, this.pendingKeyMatter];
    this.pendingKeyMatter = null;
    this.keyMatterAutocomplete.searchControl.setValue('');
    this.form.markAsDirty();
  }

  onDeleteKeyMatter(matter: AuditKeyMatter): void {
    this.selectedKeyMatters = this.selectedKeyMatters.filter(m => m.id !== matter.id);
    this.form.markAsDirty();
  }

  private performSave(): void {
    if (!this.companyId) return;

    const request: UpsertAuditReportRequest = {
      year: parseInt(this.selectedYear),
      accountingStandardId: this.form.value.accountingStandardId || null,
      auditOpinionId: this.form.value.auditOpinionId || null,
      auditOpinionModifiedId: this.form.value.auditOpinionModifiedId || null,
      // '(None)' is a real state, not a "No" — it must round-trip as null so the
      // export emits NULL rather than 0.
      hasGoingConcern: this.form.value.hasGoingConcern === 'yes' ? true
        : this.form.value.hasGoingConcern === 'no' ? false
        : null,
      materialityValue: this.form.value.materialityValue !== '' && this.form.value.materialityValue != null ? parseFloat(this.form.value.materialityValue) : null,
      auditMaterialityId: this.form.value.auditMaterialityId || null,
      opinionQualifiedIds: this.isQualified
        ? this.selectedQualifiedOptions.map(o => o.id)
        : [],
      emphasisOfMatterIds: this.selectedEmphasisOfMatters.map(m => m.id),
      keyMatterIds: this.selectedKeyMatters.map(m => m.id),
      goingConcernIds: this.isGoingConcernYes
        ? this.selectedGoingConcerns.map(c => c.id)
        : []
    };

    // Build keyword upsert requests
    const keywordsValue = this.form.get('keywords')!.value;
    const keywordRequests: CompanyKeywordUpdateRequest[] = buildKeywordRequests(keywordsValue, [
      { formField: 'auditor', type: KeywordType.AuditReportAuditor },
      { formField: 'accountingStandard', type: KeywordType.AuditReportAccountingStandard },
      { formField: 'emphasisOfMatters', type: KeywordType.AuditReportEmphasisOfMatters },
      { formField: 'keyAuditMatters', type: KeywordType.AuditReportKeyAuditMatters }
    ], this.keywords);

    const hasReportData = this.form.get('accountingStandardId')!.dirty ||
      this.form.get('auditOpinionId')!.dirty ||
      this.form.get('auditOpinionModifiedId')!.dirty ||
      this.form.get('hasGoingConcern')!.dirty ||
      this.form.get('materialityValue')!.dirty ||
      this.form.get('auditMaterialityId')!.dirty ||
      this.form.dirty; // covers list changes that mark form dirty
    const hasKeywordData = keywordRequests.length > 0 || this.keywords.some(
      k => k.type === KeywordType.AuditReportAuditor ||
           k.type === KeywordType.AuditReportAccountingStandard ||
           k.type === KeywordType.AuditReportEmphasisOfMatters ||
           k.type === KeywordType.AuditReportKeyAuditMatters
    );

    if (!hasReportData && !hasKeywordData) {
      this.toast.info('No data to save');
      return;
    }

    const description = this.getSaveDescription();
    this.spinnerService.show();

    const saveCalls: Observable<any>[] = [];
    saveCalls.push(hasReportData
      ? this.auditReportService.upsert(this.companyId, request)
      : of(null));
    saveTabThenKeywords(saveCalls, this.companyKeywordService.upsert(this.companyId!, keywordRequests)).subscribe({
      next: ([_reportResult, keywordsResult]) => {
        if (hasReportData) {
          this.showYearWarning = false;
        }
        this.keywordsUpdated.emit(keywordsResult as CompanyKeywordDto[]);
        this.form.markAsPristine();
        this.spinnerService.hide();
        this.toast.success(`Saved ${description} successfully`);
      },
      error: (err) => {
        this.spinnerService.hide();
        this.toast.error(serverErrorMessage(err, `Failed to save ${description}`));
        console.error('Save error:', err);
      }
    });
  }

  private loadLookupData(): void {
    this.spinnerService.show();

    forkJoin([
      this.lutService.getAccountingStandards(),
      this.lutService.getAuditOpinions(),
      this.lutService.getAuditOpinionModifieds(),
      this.lutService.getAuditOpinionQualifiedOptions(),
      this.lutService.getAuditEmphasisOfMatters(),
      this.lutService.getAuditKeyMattersPaginated(1, 10000, 'name', true).pipe(map(r => r.data)),
      this.lutService.getAuditGoingConcerns(),
      this.lutService.getAuditMaterialities()
    ]).subscribe({
      next: ([standards, opinions, modifieds, qualifiedOptions, emphasisOfMatters, keyMatters, goingConcerns, materialities]) => {
        this.accountingStandards = standards.sort((a, b) => a.name.localeCompare(b.name));
        this.auditOpinions = opinions.sort((a, b) => a.name.localeCompare(b.name));
        this.auditOpinionModifieds = modifieds.sort((a, b) => a.name.localeCompare(b.name));
        this.allQualifiedOptions = qualifiedOptions.sort((a, b) => a.name.localeCompare(b.name));
        this.allEmphasisOfMatters = emphasisOfMatters.sort((a, b) => a.name.localeCompare(b.name));
        this.allKeyMatters = keyMatters.sort((a, b) => a.name.localeCompare(b.name));
        this.allGoingConcerns = goingConcerns.sort((a, b) => a.name.localeCompare(b.name));
        this.auditMaterialities = materialities.sort((a, b) => a.name.localeCompare(b.name));
        this.loadData();
      },
      error: (err) => {
        this.spinnerService.hide();
        this.toast.error('Failed to load lookup data');
        console.error('Error loading lookup data:', err);
      }
    });
  }

  private loadData(): void {
    if (!this.companyId) {
      this.spinnerService.hide();
      return;
    }

    this.spinnerService.show();
    this.auditReportService.get(this.companyId, parseInt(this.selectedYear)).subscribe({
      next: resource => {
        const resolved = handleYearlyResource(resource);
        if (resolved.formValues) {
          this.patchForm(resolved.formValues);
          this.showYearWarning = false;
        } else {
          this.resetForm();
          this.showYearWarning = resolved.isAbsent;
        }
        this.spinnerService.hide();
      },
      error: (error: any) => {
        this.spinnerService.hide();
        const apiError = error.error as ApiErrorResponse;

        if (apiError?.errorCode === ErrorCodes.NFO_ENTITY_NOT_FOUND) {
          this.resetForm();
        } else {
          this.toast.error('Error loading audit report');
        }

        console.error('Audit report error:', error);
      }
    });
  }

  private patchForm(data: CompanyAuditReportDto): void {
    const goingConcernValue = data.hasGoingConcern === true ? 'yes' : data.hasGoingConcern === false ? 'no' : '';

    this.form.patchValue({
      accountingStandardId: data.accountingStandardId ?? '',
      auditOpinionId: data.auditOpinionId ?? '',
      auditOpinionModifiedId: data.auditOpinionModifiedId ?? '',
      hasGoingConcern: goingConcernValue,
      materialityValue: data.materialityValue ?? '',
      auditMaterialityId: data.auditMaterialityId ?? ''
    }, { emitEvent: false });

    this.selectedQualifiedOptions = this.sortByName(
      (data.opinionQualifieds ?? [])
        .map(item => this.allQualifiedOptions.find(o => o.id === item.lookupId))
        .filter((o): o is AuditOpinionQualifiedOption => !!o)
    );

    this.selectedEmphasisOfMatters = this.sortByName(
      (data.emphasisOfMatters ?? [])
        .map(item => this.allEmphasisOfMatters.find(m => m.id === item.lookupId))
        .filter((m): m is AuditEmphasisOfMatter => !!m)
    );

    this.selectedGoingConcerns = this.sortByName(
      (data.goingConcerns ?? [])
        .map(item => this.allGoingConcerns.find(c => c.id === item.lookupId))
        .filter((c): c is AuditGoingConcern => !!c)
    );

    // Preserve the backend's order (by stored Position) — key matters are not alphabetised.
    this.selectedKeyMatters = (data.keyMatters ?? [])
      .map(item => this.allKeyMatters.find(m => m.id === item.lookupId))
      .filter((m): m is AuditKeyMatter => !!m);

    this.syncOpinionModifiedState();
    this.form.markAsPristine();
  }

  private sortByName<T extends { name: string }>(items: T[]): T[] {
    return [...items].sort((a, b) => a.name.localeCompare(b.name));
  }

  private resetForm(): void {
    const keywordsValue = this.form.get('keywords')!.value;
    this.form.reset({
      accountingStandardId: '',
      auditOpinionId: '',
      auditOpinionModifiedId: '',
      hasGoingConcern: '',
      materialityValue: '',
      auditMaterialityId: '',
      keywords: keywordsValue
    }, { emitEvent: false });

    this.selectedQualifiedOptions = [];
    this.selectedEmphasisOfMatters = [];
    this.selectedGoingConcerns = [];
    this.selectedKeyMatters = [];
    this.syncOpinionModifiedState();
    this.form.markAsPristine();
  }
}
